import { Locales } from '@griply/types';
import { LocalizedText } from '@griply/utils';
import imagePage3 from 'shared/assets/images/whats-new/3.53.0-contributes-to.jpg';
import imagePage3x2 from 'shared/assets/images/whats-new/3.53.0-contributes-to@2x.jpg';
import imagePage3x3 from 'shared/assets/images/whats-new/3.53.0-contributes-to@3x.jpg';
import imagePage2 from 'shared/assets/images/whats-new/3.53.0-goal-roadmap.jpg';
import imagePage2x2 from 'shared/assets/images/whats-new/3.53.0-goal-roadmap@2x.jpg';
import imagePage2x3 from 'shared/assets/images/whats-new/3.53.0-goal-roadmap@3x.jpg';
import imagePage1 from 'shared/assets/images/whats-new/3.53.0-projects.jpg';
import imagePage1x2 from 'shared/assets/images/whats-new/3.53.0-projects@2x.jpg';
import imagePage1x3 from 'shared/assets/images/whats-new/3.53.0-projects@3x.jpg';
import imagePage4 from 'shared/assets/images/whats-new/3.53.0-timeline.jpg';
import imagePage4x2 from 'shared/assets/images/whats-new/3.53.0-timeline@2x.jpg';
import imagePage4x3 from 'shared/assets/images/whats-new/3.53.0-timeline@3x.jpg';
import imagePage6 from 'shared/assets/images/whats-new/3.53.0-upcoming-calendar.jpg';
import imagePage6x2 from 'shared/assets/images/whats-new/3.53.0-upcoming-calendar@2x.jpg';
import imagePage6x3 from 'shared/assets/images/whats-new/3.53.0-upcoming-calendar@3x.jpg';
import imagePage5 from 'shared/assets/images/whats-new/3.53.0-upcoming-list.jpg';
import imagePage5x2 from 'shared/assets/images/whats-new/3.53.0-upcoming-list@2x.jpg';
import imagePage5x3 from 'shared/assets/images/whats-new/3.53.0-upcoming-list@3x.jpg';

// Deliberate mobile parallel — `image`/`imageSrcSet` are platform-divergent and the content is not shared.
export type WhatsNewBullet = {
  title: LocalizedText;
  description?: LocalizedText;
};

export type WhatsNewPage = {
  image?: string;
  imageSrcSet?: string;
  newBadge?: boolean;
  title: LocalizedText;
  subtitle?: LocalizedText;
  bullets: WhatsNewBullet[];
  link?: { url: string; title: LocalizedText };
};

export type WhatsNewContent = {
  version: string;
  pages: WhatsNewPage[];
};

// The dialog is keyed to `version`, NOT the app's build version. Each user is
// shown a given `version` exactly once (tracked in the profile). Rules:
//
// - New notes to show      → bump `version` AND replace the pages.
// - Same notes, new build   → leave `version` (hot fix, patch, phased reship).
//   This is how patch releases (3.xx.1 / 3.xx.2) are skipped: leave `version`.
// - No dialog at all        → set the whole value to `null`.
//
// Bumping `version` re-shows the dialog to everyone who already saw the old one;
// leaving it still reaches anyone who skipped the prior build.
export const WHATS_NEW_CONTENT: WhatsNewContent | null = {
  version: '3.53.0',
  pages: [
    {
      image: imagePage1,
      imageSrcSet: `${imagePage1} 1x, ${imagePage1x2} 2x, ${imagePage1x3} 3x`,
      newBadge: true,
      title: {
        [Locales.EnUS]: 'Projects',
        [Locales.NlNL]: 'Projecten',
        [Locales.DeDE]: 'Projekte',
        [Locales.EsES]: 'Proyectos',
        [Locales.PlPL]: 'Projekty',
        [Locales.DaDK]: 'Projekter',
        [Locales.SvSE]: 'Projekt',
        [Locales.NbNO]: 'Prosjekter',
        [Locales.ItIT]: 'Progetti',
        [Locales.FrFR]: 'Projets',
        [Locales.PtPT]: 'Projetos',
        [Locales.PtBR]: 'Projetos',
      },
      subtitle: {
        [Locales.EnUS]:
          'Not everything is a goal in itself, and now there is a dedicated place for it.',
        [Locales.NlNL]:
          'Niet alles is op zichzelf een doel, en daar is nu een eigen plek voor.',
        [Locales.DeDE]:
          'Nicht alles ist ein Ziel an sich – dafür gibt es jetzt einen eigenen Platz.',
        [Locales.EsES]:
          'No todo es una meta en sí misma, y ahora tiene un lugar propio.',
        [Locales.PlPL]:
          'Nie wszystko jest celem samym w sobie – teraz ma to swoje miejsce.',
        [Locales.DaDK]:
          'Ikke alt er et mål i sig selv, og nu er der et sted til det.',
        [Locales.SvSE]:
          'Allt är inte ett mål i sig, och nu finns det en egen plats för det.',
        [Locales.NbNO]:
          'Ikke alt er et mål i seg selv, og nå har det fått sin egen plass.',
        [Locales.ItIT]:
          "Non tutto è un obiettivo in sé, e ora c'è un posto dedicato.",
        [Locales.FrFR]:
          "Tout n'est pas un objectif en soi, et il y a désormais une place dédiée pour cela.",
        [Locales.PtPT]:
          'Nem tudo é um objetivo em si, e agora há um lugar próprio para isso.',
        [Locales.PtBR]:
          'Nem tudo é uma meta em si, e agora existe um lugar dedicado para isso.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'Rule of thumb',
            [Locales.NlNL]: 'Vuistregel',
            [Locales.DeDE]: 'Faustregel',
            [Locales.EsES]: 'Regla general',
            [Locales.PlPL]: 'Zasada ogólna',
            [Locales.DaDK]: 'Tommelfingerregel',
            [Locales.SvSE]: 'Tumregel',
            [Locales.NbNO]: 'Tommelfingerregel',
            [Locales.ItIT]: 'Regola generale',
            [Locales.FrFR]: 'Règle de base',
            [Locales.PtPT]: 'Regra geral',
            [Locales.PtBR]: 'Regra geral',
          },
          description: {
            [Locales.EnUS]:
              'Goals are measurable outcomes. Projects are lists of tasks that move a goal forward.',
            [Locales.NlNL]:
              'Doelen zijn meetbare uitkomsten. Projecten zijn takenlijsten die een doel vooruithelpen.',
            [Locales.DeDE]:
              'Ziele sind messbare Ergebnisse. Projekte sind Aufgabenlisten, die ein Ziel voranbringen.',
            [Locales.EsES]:
              'Las metas son resultados medibles. Los proyectos son listas de tareas que hacen avanzar una meta.',
            [Locales.PlPL]:
              'Cele to mierzalne rezultaty. Projekty to listy zadań, które przybliżają cel.',
            [Locales.DaDK]:
              'Mål er målbare resultater. Projekter er lister af opgaver, der bringer et mål fremad.',
            [Locales.SvSE]:
              'Mål är mätbara resultat. Projekt är listor med uppgifter som för ett mål framåt.',
            [Locales.NbNO]:
              'Mål er målbare resultater. Prosjekter er lister med oppgaver som bringer et mål fremover.',
            [Locales.ItIT]:
              'Gli obiettivi sono risultati misurabili. I progetti sono elenchi di attività che fanno avanzare un obiettivo.',
            [Locales.FrFR]:
              'Les objectifs sont des résultats mesurables. Les projets sont des listes de tâches qui font avancer un objectif.',
            [Locales.PtPT]:
              'Os objetivos são resultados mensuráveis. Os projetos são listas de tarefas que fazem avançar um objetivo.',
            [Locales.PtBR]:
              'Metas são resultados mensuráveis. Projetos são listas de tarefas que fazem uma meta avançar.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Example',
            [Locales.NlNL]: 'Voorbeeld',
            [Locales.DeDE]: 'Beispiel',
            [Locales.EsES]: 'Ejemplo',
            [Locales.PlPL]: 'Przykład',
            [Locales.DaDK]: 'Eksempel',
            [Locales.SvSE]: 'Exempel',
            [Locales.NbNO]: 'Eksempel',
            [Locales.ItIT]: 'Esempio',
            [Locales.FrFR]: 'Exemple',
            [Locales.PtPT]: 'Exemplo',
            [Locales.PtBR]: 'Exemplo',
          },
          description: {
            [Locales.EnUS]:
              '"Reach €1M revenue" is a goal, and "Launch website" is a project under it.',
            [Locales.NlNL]:
              '"€1M omzet halen" is een doel, en "Website lanceren" is een project daaronder.',
            [Locales.DeDE]:
              '„1 Mio. € Umsatz erreichen“ ist ein Ziel, „Website launchen“ ein Projekt darunter.',
            [Locales.EsES]:
              '"Alcanzar 1 M€ de ingresos" es una meta y "Lanzar la web" es un proyecto dentro de ella.',
            [Locales.PlPL]:
              '„Osiągnąć 1 mln € przychodu” to cel, a „Uruchomić stronę” to projekt w jego ramach.',
            [Locales.DaDK]:
              '"Nå 1 mio. € i omsætning" er et mål, og "Lancér hjemmeside" er et projekt under det.',
            [Locales.SvSE]:
              '"Nå 1 miljon € i omsättning" är ett mål, och "Lansera webbplats" är ett projekt under det.',
            [Locales.NbNO]:
              '"Nå 1 million € i omsetning" er et mål, og "Lansere nettside" er et prosjekt under det.',
            [Locales.ItIT]:
              '"Raggiungere 1 M€ di fatturato" è un obiettivo, "Lanciare il sito web" è un progetto al suo interno.',
            [Locales.FrFR]:
              "« Atteindre 1 M€ de chiffre d'affaires » est un objectif, et « Lancer le site web » un projet qui en dépend.",
            [Locales.PtPT]:
              '"Atingir 1 M€ de receita" é um objetivo, e "Lançar o site" é um projeto dentro dele.',
            [Locales.PtBR]:
              '"Alcançar € 1 milhão em receita" é uma meta, e "Lançar o site" é um projeto dentro dela.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Turn into project',
            [Locales.NlNL]: 'Omzetten naar project',
            [Locales.DeDE]: 'In Projekt umwandeln',
            [Locales.EsES]: 'Convertir en proyecto',
            [Locales.PlPL]: 'Zamień w projekt',
            [Locales.DaDK]: 'Lav om til projekt',
            [Locales.SvSE]: 'Gör om till projekt',
            [Locales.NbNO]: 'Gjør om til prosjekt',
            [Locales.ItIT]: 'Trasforma in progetto',
            [Locales.FrFR]: 'Transformer en projet',
            [Locales.PtPT]: 'Transformar em projeto',
            [Locales.PtBR]: 'Transformar em projeto',
          },
          description: {
            [Locales.EnUS]: 'Right-click a goal to turn it into a project.',
            [Locales.NlNL]:
              'Klik met de rechtermuisknop op een doel om het om te zetten naar een project.',
            [Locales.DeDE]:
              'Klicke mit der rechten Maustaste auf ein Ziel, um es in ein Projekt umzuwandeln.',
            [Locales.EsES]:
              'Haz clic derecho en una meta para convertirla en proyecto.',
            [Locales.PlPL]:
              'Kliknij cel prawym przyciskiem myszy, aby zamienić go w projekt.',
            [Locales.DaDK]:
              'Højreklik på et mål for at lave det om til et projekt.',
            [Locales.SvSE]:
              'Högerklicka på ett mål för att göra om det till ett projekt.',
            [Locales.NbNO]:
              'Høyreklikk på et mål for å gjøre det om til et prosjekt.',
            [Locales.ItIT]:
              'Fai clic destro su un obiettivo per trasformarlo in un progetto.',
            [Locales.FrFR]:
              'Fais un clic droit sur un objectif pour le transformer en projet.',
            [Locales.PtPT]:
              'Clica com o botão direito num objetivo para o transformar em projeto.',
            [Locales.PtBR]:
              'Clique com o botão direito em uma meta para transformá-la em projeto.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'GTD / OKR',
            [Locales.NlNL]: 'GTD / OKR',
            [Locales.DeDE]: 'GTD / OKR',
            [Locales.EsES]: 'GTD / OKR',
            [Locales.PlPL]: 'GTD / OKR',
            [Locales.DaDK]: 'GTD / OKR',
            [Locales.SvSE]: 'GTD / OKR',
            [Locales.NbNO]: 'GTD / OKR',
            [Locales.ItIT]: 'GTD / OKR',
            [Locales.FrFR]: 'GTD / OKR',
            [Locales.PtPT]: 'GTD / OKR',
            [Locales.PtBR]: 'GTD / OKR',
          },
          description: {
            [Locales.EnUS]:
              'With Projects, Griply now has all GTD horizons and OKR layers.',
            [Locales.NlNL]:
              'Met Projecten heeft Griply nu alle GTD-horizonten en OKR-lagen.',
            [Locales.DeDE]:
              'Mit Projekten hat Griply jetzt alle GTD-Horizonte und OKR-Ebenen.',
            [Locales.EsES]:
              'Con Proyectos, Griply ya cubre todos los horizontes GTD y niveles OKR.',
            [Locales.PlPL]:
              'Dzięki Projektom Griply ma teraz wszystkie horyzonty GTD i poziomy OKR.',
            [Locales.DaDK]:
              'Med Projekter har Griply nu alle GTD-horisonter og OKR-lag.',
            [Locales.SvSE]:
              'Med Projekt har Griply nu alla GTD-horisonter och OKR-nivåer.',
            [Locales.NbNO]:
              'Med Prosjekter har Griply nå alle GTD-horisonter og OKR-nivåer.',
            [Locales.ItIT]:
              'Con i Progetti, Griply copre ora tutti gli orizzonti GTD e i livelli OKR.',
            [Locales.FrFR]:
              'Avec les Projets, Griply couvre désormais tous les horizons GTD et les niveaux OKR.',
            [Locales.PtPT]:
              'Com os Projetos, o Griply passa a ter todos os horizontes GTD e níveis OKR.',
            [Locales.PtBR]:
              'Com os Projetos, o Griply agora tem todos os horizontes GTD e níveis OKR.',
          },
        },
      ],
      link: {
        url: 'https://griply.app/help/get-started/projects-in-griply',
        title: {
          [Locales.EnUS]: 'Learn more: Projects vs Goals',
          [Locales.NlNL]: 'Meer over projecten vs doelen',
          [Locales.DeDE]: 'Mehr zu Projekten vs. Zielen',
          [Locales.EsES]: 'Más sobre proyectos y metas',
          [Locales.PlPL]: 'Więcej o projektach i celach',
          [Locales.DaDK]: 'Læs mere: Projekter vs. mål',
          [Locales.SvSE]: 'Läs mer: Projekt vs. mål',
          [Locales.NbNO]: 'Les mer: Prosjekter vs. mål',
          [Locales.ItIT]: 'Scopri di più: Progetti e obiettivi',
          [Locales.FrFR]: 'En savoir plus : Projets vs objectifs',
          [Locales.PtPT]: 'Saber mais: Projetos vs. objetivos',
          [Locales.PtBR]: 'Saiba mais: Projetos vs. metas',
        },
      },
    },
    {
      image: imagePage2,
      imageSrcSet: `${imagePage2} 1x, ${imagePage2x2} 2x, ${imagePage2x3} 3x`,
      newBadge: true,
      title: {
        [Locales.EnUS]: 'Break a goal into subgoals and projects',
        [Locales.NlNL]: 'Splits een doel op in subdoelen en projecten',
        [Locales.DeDE]: 'Ein Ziel in Unterziele und Projekte aufteilen',
        [Locales.EsES]: 'Divide una meta en submetas y proyectos',
        [Locales.PlPL]: 'Podziel cel na podcele i projekty',
        [Locales.DaDK]: 'Del et mål op i delmål og projekter',
        [Locales.SvSE]: 'Dela upp ett mål i delmål och projekt',
        [Locales.NbNO]: 'Del opp et mål i delmål og prosjekter',
        [Locales.ItIT]: 'Suddividi un obiettivo in sotto-obiettivi e progetti',
        [Locales.FrFR]: 'Découpe un objectif en sous-objectifs et projets',
        [Locales.PtPT]: 'Divide um objetivo em subobjetivos e projetos',
        [Locales.PtBR]: 'Divida uma meta em submetas e projetos',
      },
      subtitle: {
        [Locales.EnUS]:
          'Subgoals to measure outcomes and projects to get there.',
        [Locales.NlNL]:
          'Subdoelen om uitkomsten te meten en projecten om er te komen.',
        [Locales.DeDE]:
          'Unterziele, um Ergebnisse zu messen, und Projekte, um dorthin zu kommen.',
        [Locales.EsES]:
          'Submetas para medir resultados y proyectos para llegar a ellos.',
        [Locales.PlPL]:
          'Podcele do mierzenia rezultatów i projekty, które do nich prowadzą.',
        [Locales.DaDK]:
          'Delmål til at måle resultater og projekter til at nå dem.',
        [Locales.SvSE]:
          'Delmål för att mäta resultat och projekt för att nå dit.',
        [Locales.NbNO]:
          'Delmål for å måle resultater og prosjekter for å komme dit.',
        [Locales.ItIT]:
          'Sotto-obiettivi per misurare i risultati e progetti per raggiungerli.',
        [Locales.FrFR]:
          'Des sous-objectifs pour mesurer les résultats et des projets pour y parvenir.',
        [Locales.PtPT]:
          'Subobjetivos para medir resultados e projetos para lá chegar.',
        [Locales.PtBR]:
          'Submetas para medir resultados e projetos para chegar lá.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'Subgoals',
            [Locales.NlNL]: 'Subdoelen',
            [Locales.DeDE]: 'Unterziele',
            [Locales.EsES]: 'Submetas',
            [Locales.PlPL]: 'Podcele',
            [Locales.DaDK]: 'Delmål',
            [Locales.SvSE]: 'Delmål',
            [Locales.NbNO]: 'Delmål',
            [Locales.ItIT]: 'Sotto-obiettivi',
            [Locales.FrFR]: 'Sous-objectifs',
            [Locales.PtPT]: 'Subobjetivos',
            [Locales.PtBR]: 'Submetas',
          },
          description: {
            [Locales.EnUS]:
              'Split a goal into measurable subgoals, each with its own progress.',
            [Locales.NlNL]:
              'Splits een doel op in meetbare subdoelen, elk met eigen voortgang.',
            [Locales.DeDE]:
              'Teile ein Ziel in messbare Unterziele auf, jedes mit eigenem Fortschritt.',
            [Locales.EsES]:
              'Divide una meta en submetas medibles, cada una con su propio progreso.',
            [Locales.PlPL]:
              'Podziel cel na mierzalne podcele, każdy z własnym postępem.',
            [Locales.DaDK]:
              'Del et mål op i målbare delmål, hver med sin egen fremgang.',
            [Locales.SvSE]:
              'Dela upp ett mål i mätbara delmål, vart och ett med egna framsteg.',
            [Locales.NbNO]:
              'Del opp et mål i målbare delmål, hvert med sin egen fremgang.',
            [Locales.ItIT]:
              'Suddividi un obiettivo in sotto-obiettivi misurabili, ciascuno con i propri progressi.',
            [Locales.FrFR]:
              'Découpe un objectif en sous-objectifs mesurables, chacun avec sa propre progression.',
            [Locales.PtPT]:
              'Divide um objetivo em subobjetivos mensuráveis, cada um com o seu próprio progresso.',
            [Locales.PtBR]:
              'Divida uma meta em submetas mensuráveis, cada uma com seu próprio progresso.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Projects',
            [Locales.NlNL]: 'Projecten',
            [Locales.DeDE]: 'Projekte',
            [Locales.EsES]: 'Proyectos',
            [Locales.PlPL]: 'Projekty',
            [Locales.DaDK]: 'Projekter',
            [Locales.SvSE]: 'Projekt',
            [Locales.NbNO]: 'Prosjekter',
            [Locales.ItIT]: 'Progetti',
            [Locales.FrFR]: 'Projets',
            [Locales.PtPT]: 'Projetos',
            [Locales.PtBR]: 'Projetos',
          },
          description: {
            [Locales.EnUS]:
              'List the work that moves the goal forward, right under it.',
            [Locales.NlNL]:
              'Zet het werk dat het doel vooruithelpt er direct onder.',
            [Locales.DeDE]:
              'Liste die Arbeit, die das Ziel voranbringt, direkt darunter auf.',
            [Locales.EsES]:
              'Anota el trabajo que hace avanzar la meta, justo debajo de ella.',
            [Locales.PlPL]:
              'Zapisz pracę, która przybliża cel, bezpośrednio pod nim.',
            [Locales.DaDK]:
              'Skriv det arbejde, der bringer målet fremad, lige under det.',
            [Locales.SvSE]:
              'Lista arbetet som för målet framåt, direkt under det.',
            [Locales.NbNO]:
              'List opp arbeidet som bringer målet fremover, rett under det.',
            [Locales.ItIT]:
              "Elenca il lavoro che fa avanzare l'obiettivo, direttamente sotto di esso.",
            [Locales.FrFR]:
              "Liste le travail qui fait avancer l'objectif, juste en dessous.",
            [Locales.PtPT]:
              'Lista o trabalho que faz avançar o objetivo, mesmo por baixo dele.',
            [Locales.PtBR]:
              'Liste o trabalho que faz a meta avançar, logo abaixo dela.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Timeline',
            [Locales.NlNL]: 'Tijdlijn',
            [Locales.DeDE]: 'Zeitachse',
            [Locales.EsES]: 'Cronograma',
            [Locales.PlPL]: 'Oś czasu',
            [Locales.DaDK]: 'Tidslinje',
            [Locales.SvSE]: 'Tidslinje',
            [Locales.NbNO]: 'Tidslinje',
            [Locales.ItIT]: 'Linea temporale',
            [Locales.FrFR]: 'Frise chronologique',
            [Locales.PtPT]: 'Linha temporal',
            [Locales.PtBR]: 'Linha do tempo',
          },
          description: {
            [Locales.EnUS]:
              'Open the Roadmap tab in a goal and switch to the timeline view.',
            [Locales.NlNL]:
              'Open het tabblad Roadmap in een doel en schakel over naar de tijdlijnweergave.',
            [Locales.DeDE]:
              'Öffne den Tab Roadmap in einem Ziel und wechsle zur Zeitachsen-Ansicht.',
            [Locales.EsES]:
              'Abre la pestaña Roadmap en una meta y cambia a la vista de cronograma.',
            [Locales.PlPL]:
              'Otwórz zakładkę Mapa drogowa w celu i przełącz na widok osi czasu.',
            [Locales.DaDK]:
              'Åbn fanen Roadmap i et mål, og skift til tidslinjevisningen.',
            [Locales.SvSE]:
              'Öppna fliken Färdplan i ett mål och växla till tidslinjevyn.',
            [Locales.NbNO]:
              'Åpne fanen Veikart i et mål og bytt til tidslinjevisningen.',
            [Locales.ItIT]:
              'Apri la scheda Roadmap in un obiettivo e passa alla vista linea temporale.',
            [Locales.FrFR]:
              "Ouvre l'onglet Roadmap dans un objectif et passe à la vue frise chronologique.",
            [Locales.PtPT]:
              'Abre o separador Roadmap num objetivo e muda para a vista de linha temporal.',
            [Locales.PtBR]:
              'Abra a aba Roteiro em uma meta e mude para a visualização de linha do tempo.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Line charts',
            [Locales.NlNL]: 'Lijngrafieken',
            [Locales.DeDE]: 'Liniendiagramme',
            [Locales.EsES]: 'Gráficos de líneas',
            [Locales.PlPL]: 'Wykresy liniowe',
            [Locales.DaDK]: 'Linjediagrammer',
            [Locales.SvSE]: 'Linjediagram',
            [Locales.NbNO]: 'Linjediagrammer',
            [Locales.ItIT]: 'Grafici a linee',
            [Locales.FrFR]: 'Graphiques en courbes',
            [Locales.PtPT]: 'Gráficos de linhas',
            [Locales.PtBR]: 'Gráficos de linha',
          },
          description: {
            [Locales.EnUS]:
              'Subgoals show their progress over time right on the Roadmap.',
            [Locales.NlNL]:
              'Subdoelen tonen hun voortgang in de tijd direct op de Roadmap.',
            [Locales.DeDE]:
              'Unterziele zeigen ihren Fortschritt im Zeitverlauf direkt auf der Roadmap.',
            [Locales.EsES]:
              'Las submetas muestran su progreso en el tiempo directamente en el Roadmap.',
            [Locales.PlPL]:
              'Podcele pokazują swój postęp w czasie bezpośrednio na Mapie drogowej.',
            [Locales.DaDK]:
              'Delmål viser deres fremgang over tid direkte på Roadmap.',
            [Locales.SvSE]:
              'Delmål visar sina framsteg över tid direkt i Färdplanen.',
            [Locales.NbNO]:
              'Delmål viser fremgangen sin over tid rett i Veikartet.',
            [Locales.ItIT]:
              'I sotto-obiettivi mostrano i loro progressi nel tempo direttamente nella Roadmap.',
            [Locales.FrFR]:
              'Les sous-objectifs affichent leur progression dans le temps directement sur la Roadmap.',
            [Locales.PtPT]:
              'Os subobjetivos mostram o seu progresso ao longo do tempo diretamente no Roadmap.',
            [Locales.PtBR]:
              'As submetas mostram seu progresso ao longo do tempo diretamente no Roteiro.',
          },
        },
      ],
      link: {
        url: 'https://griply.app/help/get-started/creating-planning-your-first-goals',
        title: {
          [Locales.EnUS]: 'Learn more: Break a goal down',
          [Locales.NlNL]: 'Meer over een doel opsplitsen',
          [Locales.DeDE]: 'Mehr zum Aufteilen eines Ziels',
          [Locales.EsES]: 'Más sobre dividir una meta',
          [Locales.PlPL]: 'Więcej o dzieleniu celu',
          [Locales.DaDK]: 'Læs mere: Del et mål op',
          [Locales.SvSE]: 'Läs mer: Dela upp ett mål',
          [Locales.NbNO]: 'Les mer: Del opp et mål',
          [Locales.ItIT]: 'Scopri di più: Suddividere un obiettivo',
          [Locales.FrFR]: 'En savoir plus : Découper un objectif',
          [Locales.PtPT]: 'Saber mais: Dividir um objetivo',
          [Locales.PtBR]: 'Saiba mais: Dividir uma meta',
        },
      },
    },
    {
      image: imagePage3,
      imageSrcSet: `${imagePage3} 1x, ${imagePage3x2} 2x, ${imagePage3x3} 3x`,
      newBadge: true,
      title: {
        [Locales.EnUS]: 'Link to multiple goals and life areas',
        [Locales.NlNL]: 'Koppel aan meerdere doelen en levensgebieden',
        [Locales.DeDE]: 'Mit mehreren Zielen und Lebensbereichen verknüpfen',
        [Locales.EsES]: 'Vincula a varias metas y áreas de vida',
        [Locales.PlPL]: 'Powiąż z wieloma celami i obszarami życia',
        [Locales.DaDK]: 'Knyt til flere mål og livsområder',
        [Locales.SvSE]: 'Koppla till flera mål och livsområden',
        [Locales.NbNO]: 'Knytt til flere mål og livsområder',
        [Locales.ItIT]: 'Collega a più obiettivi e aree di vita',
        [Locales.FrFR]: 'Relie à plusieurs objectifs et domaines de vie',
        [Locales.PtPT]: 'Liga a vários objetivos e áreas de vida',
        [Locales.PtBR]: 'Vincule a várias metas e áreas da vida',
      },
      subtitle: {
        [Locales.EnUS]:
          'A habit, goal or project can contribute to multiple goals or life areas.',
        [Locales.NlNL]:
          'Een gewoonte, doel of project kan bijdragen aan meerdere doelen of levensgebieden.',
        [Locales.DeDE]:
          'Eine Gewohnheit, ein Ziel oder ein Projekt kann zu mehreren Zielen oder Lebensbereichen beitragen.',
        [Locales.EsES]:
          'Un hábito, una meta o un proyecto puede contribuir a varias metas o áreas de vida.',
        [Locales.PlPL]:
          'Nawyk, cel lub projekt może przyczyniać się do wielu celów lub obszarów życia.',
        [Locales.DaDK]:
          'En vane, et mål eller et projekt kan bidrage til flere mål eller livsområder.',
        [Locales.SvSE]:
          'En vana, ett mål eller ett projekt kan bidra till flera mål eller livsområden.',
        [Locales.NbNO]:
          'En vane, et mål eller et prosjekt kan bidra til flere mål eller livsområder.',
        [Locales.ItIT]:
          "Un'abitudine, un obiettivo o un progetto può contribuire a più obiettivi o aree di vita.",
        [Locales.FrFR]:
          'Une habitude, un objectif ou un projet peut contribuer à plusieurs objectifs ou domaines de vie.',
        [Locales.PtPT]:
          'Um hábito, objetivo ou projeto pode contribuir para vários objetivos ou áreas de vida.',
        [Locales.PtBR]:
          'Um hábito, meta ou projeto pode contribuir para várias metas ou áreas da vida.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'Goals',
            [Locales.NlNL]: 'Doelen',
            [Locales.DeDE]: 'Ziele',
            [Locales.EsES]: 'Metas',
            [Locales.PlPL]: 'Cele',
            [Locales.DaDK]: 'Mål',
            [Locales.SvSE]: 'Mål',
            [Locales.NbNO]: 'Mål',
            [Locales.ItIT]: 'Obiettivi',
            [Locales.FrFR]: 'Objectifs',
            [Locales.PtPT]: 'Objetivos',
            [Locales.PtBR]: 'Metas',
          },
          description: {
            [Locales.EnUS]:
              'Put a goal under two life areas, or make it a subgoal of more than one goal.',
            [Locales.NlNL]:
              'Zet een doel onder twee levensgebieden, of maak het een subdoel van meer dan één doel.',
            [Locales.DeDE]:
              'Ordne ein Ziel zwei Lebensbereichen zu oder mache es zum Unterziel mehrerer Ziele.',
            [Locales.EsES]:
              'Coloca una meta en dos áreas de vida, o hazla submeta de más de una meta.',
            [Locales.PlPL]:
              'Umieść cel w dwóch obszarach życia lub uczyń go podcelem więcej niż jednego celu.',
            [Locales.DaDK]:
              'Placér et mål under to livsområder, eller gør det til delmål for mere end ét mål.',
            [Locales.SvSE]:
              'Lägg ett mål under två livsområden, eller gör det till delmål för mer än ett mål.',
            [Locales.NbNO]:
              'Legg et mål under to livsområder, eller gjør det til delmål for mer enn ett mål.',
            [Locales.ItIT]:
              'Metti un obiettivo sotto due aree di vita, o rendilo sotto-obiettivo di più di un obiettivo.',
            [Locales.FrFR]:
              'Place un objectif sous deux domaines de vie, ou fais-en un sous-objectif de plusieurs objectifs.',
            [Locales.PtPT]:
              'Coloca um objetivo em duas áreas de vida, ou torna-o subobjetivo de mais do que um objetivo.',
            [Locales.PtBR]:
              'Coloque uma meta em duas áreas da vida, ou faça dela uma submeta de mais de uma meta.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Projects',
            [Locales.NlNL]: 'Projecten',
            [Locales.DeDE]: 'Projekte',
            [Locales.EsES]: 'Proyectos',
            [Locales.PlPL]: 'Projekty',
            [Locales.DaDK]: 'Projekter',
            [Locales.SvSE]: 'Projekt',
            [Locales.NbNO]: 'Prosjekter',
            [Locales.ItIT]: 'Progetti',
            [Locales.FrFR]: 'Projets',
            [Locales.PtPT]: 'Projetos',
            [Locales.PtBR]: 'Projetos',
          },
          description: {
            [Locales.EnUS]:
              "Link one project to every goal it moves forward, and it shows up in each goal's Roadmap.",
            [Locales.NlNL]:
              'Koppel een project aan elk doel dat het vooruithelpt, en het verschijnt in de Roadmap van elk doel.',
            [Locales.DeDE]:
              'Verknüpfe ein Projekt mit jedem Ziel, das es voranbringt, und es erscheint in der Roadmap jedes Ziels.',
            [Locales.EsES]:
              'Vincula un proyecto a todas las metas que impulsa y aparecerá en el Roadmap de cada una.',
            [Locales.PlPL]:
              'Powiąż projekt z każdym celem, który przybliża, a pojawi się w Mapie drogowej każdego z nich.',
            [Locales.DaDK]:
              'Knyt ét projekt til hvert mål, det bringer fremad, og det vises i hvert måls Roadmap.',
            [Locales.SvSE]:
              'Koppla ett projekt till varje mål det för framåt, så visas det i varje måls Färdplan.',
            [Locales.NbNO]:
              'Knytt ett prosjekt til hvert mål det bringer fremover, så vises det i Veikartet til hvert mål.',
            [Locales.ItIT]:
              'Collega un progetto a ogni obiettivo che fa avanzare, e apparirà nella Roadmap di ciascuno.',
            [Locales.FrFR]:
              "Relie un projet à chaque objectif qu'il fait avancer, et il apparaît dans la Roadmap de chacun.",
            [Locales.PtPT]:
              'Liga um projeto a todos os objetivos que faz avançar, e ele aparece no Roadmap de cada um.',
            [Locales.PtBR]:
              'Vincule um projeto a todas as metas que ele faz avançar, e ele aparece no Roteiro de cada uma.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Habits',
            [Locales.NlNL]: 'Gewoontes',
            [Locales.DeDE]: 'Gewohnheiten',
            [Locales.EsES]: 'Hábitos',
            [Locales.PlPL]: 'Nawyki',
            [Locales.DaDK]: 'Vaner',
            [Locales.SvSE]: 'Vanor',
            [Locales.NbNO]: 'Vaner',
            [Locales.ItIT]: 'Abitudini',
            [Locales.FrFR]: 'Habitudes',
            [Locales.PtPT]: 'Hábitos',
            [Locales.PtBR]: 'Hábitos',
          },
          description: {
            [Locales.EnUS]:
              'Link a habit like a morning run to more than one goal at the same time.',
            [Locales.NlNL]:
              'Koppel een gewoonte zoals een ochtendrun tegelijk aan meer dan één doel.',
            [Locales.DeDE]:
              'Verknüpfe eine Gewohnheit wie den Morgenlauf gleichzeitig mit mehreren Zielen.',
            [Locales.EsES]:
              'Vincula un hábito, como salir a correr por la mañana, a más de una meta a la vez.',
            [Locales.PlPL]:
              'Powiąż nawyk, np. poranny bieg, z więcej niż jednym celem jednocześnie.',
            [Locales.DaDK]:
              'Knyt en vane som en morgenløbetur til mere end ét mål på samme tid.',
            [Locales.SvSE]:
              'Koppla en vana som en morgonlöprunda till mer än ett mål samtidigt.',
            [Locales.NbNO]:
              'Knytt en vane som en morgenløpetur til mer enn ett mål samtidig.',
            [Locales.ItIT]:
              "Collega un'abitudine come la corsa mattutina a più di un obiettivo contemporaneamente.",
            [Locales.FrFR]:
              'Relie une habitude comme un footing matinal à plusieurs objectifs à la fois.',
            [Locales.PtPT]:
              'Liga um hábito, como a corrida matinal, a mais do que um objetivo ao mesmo tempo.',
            [Locales.PtBR]:
              'Vincule um hábito, como a corrida matinal, a mais de uma meta ao mesmo tempo.',
          },
        },
      ],
    },
    {
      image: imagePage4,
      imageSrcSet: `${imagePage4} 1x, ${imagePage4x2} 2x, ${imagePage4x3} 3x`,
      newBadge: false,
      title: {
        [Locales.EnUS]: 'Timeline improvements',
        [Locales.NlNL]: 'Verbeteringen aan de tijdlijn',
        [Locales.DeDE]: 'Verbesserungen an der Zeitachse',
        [Locales.EsES]: 'Mejoras en el cronograma',
        [Locales.PlPL]: 'Ulepszenia osi czasu',
        [Locales.DaDK]: 'Forbedringer af tidslinjen',
        [Locales.SvSE]: 'Förbättringar av tidslinjen',
        [Locales.NbNO]: 'Forbedringer av tidslinjen',
        [Locales.ItIT]: 'Miglioramenti alla linea temporale',
        [Locales.FrFR]: 'Améliorations de la frise chronologique',
        [Locales.PtPT]: 'Melhorias na linha temporal',
        [Locales.PtBR]: 'Melhorias na linha do tempo',
      },
      subtitle: {
        [Locales.EnUS]:
          'See your goals and projects over time and make a plan you can keep.',
        [Locales.NlNL]:
          'Bekijk je doelen en projecten in de tijd en maak een plan dat je kunt volhouden.',
        [Locales.DeDE]:
          'Sieh deine Ziele und Projekte im Zeitverlauf und mach einen Plan, den du einhalten kannst.',
        [Locales.EsES]:
          'Ve tus metas y proyectos en el tiempo y haz un plan que puedas cumplir.',
        [Locales.PlPL]:
          'Zobacz swoje cele i projekty w czasie i ułóż plan, którego się trzymasz.',
        [Locales.DaDK]:
          'Se dine mål og projekter over tid, og læg en plan, du kan holde.',
        [Locales.SvSE]:
          'Se dina mål och projekt över tid och gör en plan du kan hålla.',
        [Locales.NbNO]:
          'Se målene og prosjektene dine over tid, og lag en plan du kan holde.',
        [Locales.ItIT]:
          'Guarda i tuoi obiettivi e progetti nel tempo e crea un piano che puoi rispettare.',
        [Locales.FrFR]:
          'Vois tes objectifs et projets dans le temps et fais un plan que tu peux tenir.',
        [Locales.PtPT]:
          'Vê os teus objetivos e projetos ao longo do tempo e faz um plano que consigas cumprir.',
        [Locales.PtBR]:
          'Veja suas metas e projetos ao longo do tempo e faça um plano que você consiga cumprir.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'A spot in the Plan section',
            [Locales.NlNL]: 'Een plek in de sectie Plan',
            [Locales.DeDE]: 'Ein Platz im Bereich Plan',
            [Locales.EsES]: 'Un lugar en la sección Plan',
            [Locales.PlPL]: 'Miejsce w sekcji Plan',
            [Locales.DaDK]: 'En plads i sektionen Planlæg',
            [Locales.SvSE]: 'En plats i avsnittet Planera',
            [Locales.NbNO]: 'En plass i seksjonen Planlegg',
            [Locales.ItIT]: 'Un posto nella sezione Pianifica',
            [Locales.FrFR]: 'Une place dans la section Planifier',
            [Locales.PtPT]: 'Um lugar na secção Planear',
            [Locales.PtBR]: 'Um lugar na seção Planejar',
          },
          description: {
            [Locales.EnUS]: 'The timeline now lives in the Plan section.',
            [Locales.NlNL]: 'De tijdlijn staat nu in de sectie Plan.',
            [Locales.DeDE]:
              'Die Zeitachse befindet sich jetzt im Bereich Plan.',
            [Locales.EsES]: 'El cronograma está ahora en la sección Plan.',
            [Locales.PlPL]: 'Oś czasu znajduje się teraz w sekcji Plan.',
            [Locales.DaDK]: 'Tidslinjen ligger nu i sektionen Planlæg.',
            [Locales.SvSE]: 'Tidslinjen ligger nu i avsnittet Planera.',
            [Locales.NbNO]: 'Tidslinjen ligger nå i seksjonen Planlegg.',
            [Locales.ItIT]:
              'La linea temporale ora si trova nella sezione Pianifica.',
            [Locales.FrFR]:
              'La frise chronologique se trouve désormais dans la section Planifier.',
            [Locales.PtPT]: 'A linha temporal está agora na secção Planear.',
            [Locales.PtBR]: 'A linha do tempo agora fica na seção Planejar.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Goals and projects',
            [Locales.NlNL]: 'Doelen en projecten',
            [Locales.DeDE]: 'Ziele und Projekte',
            [Locales.EsES]: 'Metas y proyectos',
            [Locales.PlPL]: 'Cele i projekty',
            [Locales.DaDK]: 'Mål og projekter',
            [Locales.SvSE]: 'Mål och projekt',
            [Locales.NbNO]: 'Mål og prosjekter',
            [Locales.ItIT]: 'Obiettivi e progetti',
            [Locales.FrFR]: 'Objectifs et projets',
            [Locales.PtPT]: 'Objetivos e projetos',
            [Locales.PtBR]: 'Metas e projetos',
          },
          description: {
            [Locales.EnUS]:
              'Your goals and projects are mapped out over time, side by side.',
            [Locales.NlNL]:
              'Je doelen en projecten staan naast elkaar uitgezet in de tijd.',
            [Locales.DeDE]:
              'Deine Ziele und Projekte sind nebeneinander im Zeitverlauf abgebildet.',
            [Locales.EsES]:
              'Tus metas y proyectos se muestran en el tiempo, uno junto a otro.',
            [Locales.PlPL]:
              'Twoje cele i projekty są rozłożone w czasie, obok siebie.',
            [Locales.DaDK]:
              'Dine mål og projekter er lagt ud over tid, side om side.',
            [Locales.SvSE]:
              'Dina mål och projekt är utlagda över tid, sida vid sida.',
            [Locales.NbNO]:
              'Målene og prosjektene dine er lagt ut over tid, side om side.',
            [Locales.ItIT]:
              "I tuoi obiettivi e progetti sono disposti nel tempo, uno accanto all'altro.",
            [Locales.FrFR]:
              'Tes objectifs et projets sont répartis dans le temps, côte à côte.',
            [Locales.PtPT]:
              'Os teus objetivos e projetos estão dispostos ao longo do tempo, lado a lado.',
            [Locales.PtBR]:
              'Suas metas e projetos são dispostos ao longo do tempo, lado a lado.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Weekend shading',
            [Locales.NlNL]: 'Weekenden gemarkeerd',
            [Locales.DeDE]: 'Wochenenden hervorgehoben',
            [Locales.EsES]: 'Fines de semana sombreados',
            [Locales.PlPL]: 'Wyróżnione weekendy',
            [Locales.DaDK]: 'Skraverede weekender',
            [Locales.SvSE]: 'Skuggade helger',
            [Locales.NbNO]: 'Skyggelagte helger',
            [Locales.ItIT]: 'Weekend evidenziati',
            [Locales.FrFR]: 'Week-ends grisés',
            [Locales.PtPT]: 'Fins de semana sombreados',
            [Locales.PtBR]: 'Fins de semana sombreados',
          },
          description: {
            [Locales.EnUS]:
              'Weekends are shaded in the month and quarter views.',
            [Locales.NlNL]:
              'Weekenden zijn gearceerd in de maand- en kwartaalweergave.',
            [Locales.DeDE]:
              'Wochenenden sind in der Monats- und Quartalsansicht schattiert.',
            [Locales.EsES]:
              'Los fines de semana aparecen sombreados en las vistas de mes y trimestre.',
            [Locales.PlPL]:
              'Weekendy są wyróżnione w widoku miesiąca i kwartału.',
            [Locales.DaDK]:
              'Weekender er skraveret i måneds- og kvartalsvisningen.',
            [Locales.SvSE]: 'Helger är skuggade i månads- och kvartalsvyn.',
            [Locales.NbNO]:
              'Helger er skyggelagt i måneds- og kvartalsvisningen.',
            [Locales.ItIT]:
              'I weekend sono ombreggiati nelle viste mese e trimestre.',
            [Locales.FrFR]:
              'Les week-ends sont grisés dans les vues mois et trimestre.',
            [Locales.PtPT]:
              'Os fins de semana aparecem sombreados nas vistas de mês e trimestre.',
            [Locales.PtBR]:
              'Os fins de semana aparecem sombreados nas visualizações de mês e trimestre.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Centered on today',
            [Locales.NlNL]: 'Gecentreerd op vandaag',
            [Locales.DeDE]: 'Auf heute zentriert',
            [Locales.EsES]: 'Centrado en hoy',
            [Locales.PlPL]: 'Wyśrodkowane na dziś',
            [Locales.DaDK]: 'Centreret om i dag',
            [Locales.SvSE]: 'Centrerad på idag',
            [Locales.NbNO]: 'Sentrert på i dag',
            [Locales.ItIT]: 'Centrato su oggi',
            [Locales.FrFR]: "Centrée sur aujourd'hui",
            [Locales.PtPT]: 'Centrado em hoje',
            [Locales.PtBR]: 'Centralizado em hoje',
          },
          description: {
            [Locales.EnUS]:
              'Today sits in the middle of the view at every zoom level.',
            [Locales.NlNL]:
              'Vandaag staat op elk zoomniveau in het midden van de weergave.',
            [Locales.DeDE]:
              'Heute liegt auf jeder Zoomstufe in der Mitte der Ansicht.',
            [Locales.EsES]:
              'Hoy queda en el centro de la vista en todos los niveles de zoom.',
            [Locales.PlPL]:
              'Dzisiejszy dzień jest na środku widoku na każdym poziomie powiększenia.',
            [Locales.DaDK]:
              'I dag ligger midt i visningen på alle zoomniveauer.',
            [Locales.SvSE]: 'Idag ligger mitt i vyn på alla zoomnivåer.',
            [Locales.NbNO]: 'I dag ligger midt i visningen på alle zoomnivåer.',
            [Locales.ItIT]:
              'Oggi è al centro della vista a ogni livello di zoom.',
            [Locales.FrFR]:
              "Aujourd'hui se trouve au centre de la vue à chaque niveau de zoom.",
            [Locales.PtPT]:
              'Hoje fica no centro da vista em todos os níveis de zoom.',
            [Locales.PtBR]:
              'Hoje fica no centro da visualização em todos os níveis de zoom.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Drag and drop',
            [Locales.NlNL]: 'Slepen en neerzetten',
            [Locales.DeDE]: 'Drag & Drop',
            [Locales.EsES]: 'Arrastrar y soltar',
            [Locales.PlPL]: 'Przeciągnij i upuść',
            [Locales.DaDK]: 'Træk og slip',
            [Locales.SvSE]: 'Dra och släpp',
            [Locales.NbNO]: 'Dra og slipp',
            [Locales.ItIT]: 'Trascina e rilascia',
            [Locales.FrFR]: 'Glisser-déposer',
            [Locales.PtPT]: 'Arrastar e largar',
            [Locales.PtBR]: 'Arrastar e soltar',
          },
          description: {
            [Locales.EnUS]:
              'Drop zones are clearer while you drag and reorder.',
            [Locales.NlNL]:
              'De plek waar je iets neerzet is duidelijker terwijl je sleept en herschikt.',
            [Locales.DeDE]:
              'Ablagebereiche sind beim Ziehen und Umsortieren deutlicher zu sehen.',
            [Locales.EsES]:
              'Las zonas de destino se ven más claras mientras arrastras y reordenas.',
            [Locales.PlPL]:
              'Miejsca upuszczania są wyraźniejsze podczas przeciągania i zmiany kolejności.',
            [Locales.DaDK]:
              'Det er tydeligere, hvor du kan slippe, mens du trækker og omarrangerer.',
            [Locales.SvSE]:
              'Släppytorna syns tydligare medan du drar och ordnar om.',
            [Locales.NbNO]:
              'Slippsonene er tydeligere mens du drar og sorterer.',
            [Locales.ItIT]:
              'Le aree di rilascio sono più chiare mentre trascini e riordini.',
            [Locales.FrFR]:
              'Les zones de dépôt sont plus claires pendant que tu déplaces et réorganises.',
            [Locales.PtPT]:
              'As zonas de largada são mais claras enquanto arrastas e reordenas.',
            [Locales.PtBR]:
              'As áreas para soltar ficam mais claras enquanto você arrasta e reordena.',
          },
        },
      ],
    },
    {
      image: imagePage5,
      imageSrcSet: `${imagePage5} 1x, ${imagePage5x2} 2x, ${imagePage5x3} 3x`,
      newBadge: false,
      title: {
        [Locales.EnUS]: 'Upcoming improvements',
        [Locales.NlNL]: 'Verbeteringen aan Aankomend',
        [Locales.DeDE]: 'Verbesserungen unter Demnächst',
        [Locales.EsES]: 'Mejoras en Próximas',
        [Locales.PlPL]: 'Ulepszenia w Nadchodzących',
        [Locales.DaDK]: 'Forbedringer af Kommende',
        [Locales.SvSE]: 'Förbättringar av Kommande',
        [Locales.NbNO]: 'Forbedringer av Kommende',
        [Locales.ItIT]: 'Miglioramenti a Prossimi',
        [Locales.FrFR]: 'Améliorations de À venir',
        [Locales.PtPT]: 'Melhorias em Próximas',
        [Locales.PtBR]: 'Melhorias em Em breve',
      },
      subtitle: {
        [Locales.EnUS]:
          'Plan your week from the list view, with the same tools the calendar has.',
        [Locales.NlNL]:
          'Plan je week vanuit de lijstweergave, met dezelfde tools als de agenda.',
        [Locales.DeDE]:
          'Plane deine Woche aus der Listenansicht, mit denselben Werkzeugen wie im Kalender.',
        [Locales.EsES]:
          'Planifica tu semana desde la vista de lista, con las mismas herramientas que el calendario.',
        [Locales.PlPL]:
          'Planuj tydzień z widoku listy, z tymi samymi narzędziami co w kalendarzu.',
        [Locales.DaDK]:
          'Planlæg din uge fra listevisningen med de samme værktøjer som kalenderen.',
        [Locales.SvSE]:
          'Planera din vecka från listvyn, med samma verktyg som kalendern har.',
        [Locales.NbNO]:
          'Planlegg uken din fra listevisningen, med de samme verktøyene som kalenderen.',
        [Locales.ItIT]:
          'Pianifica la tua settimana dalla vista elenco, con gli stessi strumenti del calendario.',
        [Locales.FrFR]:
          'Planifie ta semaine depuis la vue liste, avec les mêmes outils que le calendrier.',
        [Locales.PtPT]:
          'Planeia a tua semana a partir da vista de lista, com as mesmas ferramentas do calendário.',
        [Locales.PtBR]:
          'Planeje sua semana pela visualização em lista, com as mesmas ferramentas do calendário.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'A spot in the Plan section',
            [Locales.NlNL]: 'Een plek in de sectie Plan',
            [Locales.DeDE]: 'Ein Platz im Bereich Plan',
            [Locales.EsES]: 'Un lugar en la sección Plan',
            [Locales.PlPL]: 'Miejsce w sekcji Plan',
            [Locales.DaDK]: 'En plads i sektionen Planlæg',
            [Locales.SvSE]: 'En plats i avsnittet Planera',
            [Locales.NbNO]: 'En plass i seksjonen Planlegg',
            [Locales.ItIT]: 'Un posto nella sezione Pianifica',
            [Locales.FrFR]: 'Une place dans la section Planifier',
            [Locales.PtPT]: 'Um lugar na secção Planear',
            [Locales.PtBR]: 'Um lugar na seção Planejar',
          },
          description: {
            [Locales.EnUS]: 'Upcoming now lives in the Plan section.',
            [Locales.NlNL]: 'Aankomend staat nu in de sectie Plan.',
            [Locales.DeDE]: 'Demnächst befindet sich jetzt im Bereich Plan.',
            [Locales.EsES]: 'Próximas está ahora en la sección Plan.',
            [Locales.PlPL]: 'Nadchodzące znajdują się teraz w sekcji Plan.',
            [Locales.DaDK]: 'Kommende ligger nu i sektionen Planlæg.',
            [Locales.SvSE]: 'Kommande ligger nu i avsnittet Planera.',
            [Locales.NbNO]: 'Kommende ligger nå i seksjonen Planlegg.',
            [Locales.ItIT]: 'Prossimi ora si trova nella sezione Pianifica.',
            [Locales.FrFR]:
              'À venir se trouve désormais dans la section Planifier.',
            [Locales.PtPT]: 'Próximas está agora na secção Planear.',
            [Locales.PtBR]: 'Em breve agora fica na seção Planejar.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Plan tasks',
            [Locales.NlNL]: 'Taken plannen',
            [Locales.DeDE]: 'Aufgaben planen',
            [Locales.EsES]: 'Planificar tareas',
            [Locales.PlPL]: 'Zaplanuj zadania',
            [Locales.DaDK]: 'Planlæg opgaver',
            [Locales.SvSE]: 'Planera uppgifter',
            [Locales.NbNO]: 'Planlegg oppgaver',
            [Locales.ItIT]: 'Pianifica attività',
            [Locales.FrFR]: 'Planifier des tâches',
            [Locales.PtPT]: 'Planear tarefas',
            [Locales.PtBR]: 'Planejar tarefas',
          },
          description: {
            [Locales.EnUS]:
              'Click "Plan tasks" to open your tasks in a column on the right, then drag each one onto a day.',
            [Locales.NlNL]:
              'Klik op "Taken plannen" om je taken in een kolom rechts te openen en sleep ze daarna elk naar een dag.',
            [Locales.DeDE]:
              'Klicke auf „Aufgaben planen“, um deine Aufgaben rechts in einer Spalte zu öffnen, und zieh sie dann auf einen Tag.',
            [Locales.EsES]:
              'Haz clic en "Planificar tareas" para abrir tus tareas en una columna a la derecha y arrastra cada una a un día.',
            [Locales.PlPL]:
              'Kliknij „Zaplanuj zadania”, aby otworzyć zadania w kolumnie po prawej, a potem przeciągnij każde na wybrany dzień.',
            [Locales.DaDK]:
              'Klik på "Planlæg opgaver" for at åbne dine opgaver i en kolonne til højre, og træk dem så én ad gangen til en dag.',
            [Locales.SvSE]:
              'Klicka på "Planera uppgifter" för att öppna dina uppgifter i en kolumn till höger och dra sedan var och en till en dag.',
            [Locales.NbNO]:
              'Klikk på "Planlegg oppgaver" for å åpne oppgavene dine i en kolonne til høyre, og dra dem så én og én til en dag.',
            [Locales.ItIT]:
              'Fai clic su "Pianifica attività" per aprire le tue attività in una colonna a destra, poi trascina ognuna su un giorno.',
            [Locales.FrFR]:
              'Clique sur « Planifier des tâches » pour ouvrir tes tâches dans une colonne à droite, puis glisse chacune sur un jour.',
            [Locales.PtPT]:
              'Clica em "Planear tarefas" para abrir as tuas tarefas numa coluna à direita e arrasta cada uma para um dia.',
            [Locales.PtBR]:
              'Clique em "Planejar tarefas" para abrir suas tarefas em uma coluna à direita e arraste cada uma para um dia.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Any source',
            [Locales.NlNL]: 'Elke bron',
            [Locales.DeDE]: 'Jede Quelle',
            [Locales.EsES]: 'Cualquier origen',
            [Locales.PlPL]: 'Dowolne źródło',
            [Locales.DaDK]: 'Enhver kilde',
            [Locales.SvSE]: 'Valfri källa',
            [Locales.NbNO]: 'Enhver kilde',
            [Locales.ItIT]: 'Qualsiasi origine',
            [Locales.FrFR]: "N'importe quelle source",
            [Locales.PtPT]: 'Qualquer origem',
            [Locales.PtBR]: 'Qualquer origem',
          },
          description: {
            [Locales.EnUS]:
              'Switch the column to Inbox, a smart list, a tag or your habits and plan from there.',
            [Locales.NlNL]:
              'Schakel de kolom over naar Inbox, een slimme lijst, een label of je gewoontes en plan van daaruit.',
            [Locales.DeDE]:
              'Schalte die Spalte auf Eingang, eine Smart-Liste, einen Tag oder deine Gewohnheiten um und plane von dort aus.',
            [Locales.EsES]:
              'Cambia la columna a Bandeja de entrada, una lista inteligente, una etiqueta o tus hábitos y planifica desde ahí.',
            [Locales.PlPL]:
              'Przełącz kolumnę na Skrzynkę, inteligentną listę, tag lub swoje nawyki i planuj stamtąd.',
            [Locales.DaDK]:
              'Skift kolonnen til Indbakke, en smart liste, et tag eller dine vaner, og planlæg derfra.',
            [Locales.SvSE]:
              'Växla kolumnen till Inkorg, en smart lista, en tagg eller dina vanor och planera därifrån.',
            [Locales.NbNO]:
              'Bytt kolonnen til Innboks, en smartliste, en tagg eller vanene dine, og planlegg derfra.',
            [Locales.ItIT]:
              'Passa la colonna a In entrata, una lista intelligente, un tag o le tue abitudini e pianifica da lì.',
            [Locales.FrFR]:
              'Passe la colonne sur Boîte de réception, une liste intelligente, une étiquette ou tes habitudes, et planifie de là.',
            [Locales.PtPT]:
              'Muda a coluna para Caixa de entrada, uma lista inteligente, uma etiqueta ou os teus hábitos e planeia a partir daí.',
            [Locales.PtBR]:
              'Mude a coluna para Caixa de entrada, uma lista inteligente, uma etiqueta ou seus hábitos e planeje a partir daí.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Goal deadlines',
            [Locales.NlNL]: 'Doeldeadlines',
            [Locales.DeDE]: 'Ziel-Deadlines',
            [Locales.EsES]: 'Fechas límite de metas',
            [Locales.PlPL]: 'Terminy celów',
            [Locales.DaDK]: 'Måldeadlines',
            [Locales.SvSE]: 'Måldeadlines',
            [Locales.NbNO]: 'Målfrister',
            [Locales.ItIT]: 'Scadenze degli obiettivi',
            [Locales.FrFR]: 'Échéances des objectifs',
            [Locales.PtPT]: 'Prazos dos objetivos',
            [Locales.PtBR]: 'Prazos das metas',
          },
          description: {
            [Locales.EnUS]:
              "A goal's deadline now shows in the Upcoming list on the day it's due.",
            [Locales.NlNL]:
              'De deadline van een doel staat nu in de lijst Aankomend op de dag dat hij verloopt.',
            [Locales.DeDE]:
              'Die Deadline eines Ziels erscheint jetzt unter Demnächst an dem Tag, an dem sie fällig ist.',
            [Locales.EsES]:
              'La fecha límite de una meta aparece ahora en la lista Próximas el día que vence.',
            [Locales.PlPL]:
              'Termin celu pojawia się teraz na liście Nadchodzące w dniu, w którym upływa.',
            [Locales.DaDK]:
              'Et måls deadline vises nu på listen Kommende den dag, det forfalder.',
            [Locales.SvSE]:
              'Ett måls deadline visas nu i listan Kommande den dag det förfaller.',
            [Locales.NbNO]:
              'Fristen til et mål vises nå i listen Kommende den dagen den forfaller.',
            [Locales.ItIT]:
              "La scadenza di un obiettivo ora compare nell'elenco Prossimi il giorno in cui scade.",
            [Locales.FrFR]:
              "L'échéance d'un objectif apparaît désormais dans la liste À venir le jour où elle arrive.",
            [Locales.PtPT]:
              'O prazo de um objetivo aparece agora na lista Próximas no dia em que termina.',
            [Locales.PtBR]:
              'O prazo de uma meta agora aparece na lista Em breve no dia em que vence.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Plus menu',
            [Locales.NlNL]: 'Plusmenu',
            [Locales.DeDE]: 'Plus-Menü',
            [Locales.EsES]: 'Menú +',
            [Locales.PlPL]: 'Menu plusa',
            [Locales.DaDK]: 'Plus-menu',
            [Locales.SvSE]: 'Plusmenyn',
            [Locales.NbNO]: 'Plussmenyen',
            [Locales.ItIT]: 'Menu +',
            [Locales.FrFR]: 'Menu +',
            [Locales.PtPT]: 'Menu +',
            [Locales.PtBR]: 'Menu +',
          },
          description: {
            [Locales.EnUS]:
              'Hit + next to a day and add a task, calendar event or focus block.',
            [Locales.NlNL]:
              'Klik op + naast een dag en voeg een taak, agenda-item of focusblok toe.',
            [Locales.DeDE]:
              'Klicke auf + neben einem Tag und füge eine Aufgabe, ein Kalenderereignis oder einen Fokusblock hinzu.',
            [Locales.EsES]:
              'Pulsa + junto a un día y añade una tarea, un evento del calendario o un bloque de enfoque.',
            [Locales.PlPL]:
              'Kliknij + obok dnia i dodaj zadanie, wydarzenie z kalendarza lub blok skupienia.',
            [Locales.DaDK]:
              'Klik på + ud for en dag, og tilføj en opgave, en kalenderbegivenhed eller en fokusblok.',
            [Locales.SvSE]:
              'Klicka på + bredvid en dag och lägg till en uppgift, kalenderhändelse eller ett fokusblock.',
            [Locales.NbNO]:
              'Klikk på + ved siden av en dag og legg til en oppgave, kalenderhendelse eller fokusblokk.',
            [Locales.ItIT]:
              "Premi + accanto a un giorno e aggiungi un'attività, un evento del calendario o un blocco di focus.",
            [Locales.FrFR]:
              "Clique sur + à côté d'un jour et ajoute une tâche, un événement du calendrier ou un créneau de concentration.",
            [Locales.PtPT]:
              'Clica em + ao lado de um dia e adiciona uma tarefa, um evento do calendário ou um bloco de foco.',
            [Locales.PtBR]:
              'Clique em + ao lado de um dia e adicione uma tarefa, um evento do calendário ou um bloco de foco.',
          },
        },
      ],
    },
    {
      image: imagePage6,
      imageSrcSet: `${imagePage6} 1x, ${imagePage6x2} 2x, ${imagePage6x3} 3x`,
      newBadge: false,
      title: {
        [Locales.EnUS]: 'Other improvements and fixes',
        [Locales.NlNL]: 'Overige verbeteringen en fixes',
        [Locales.DeDE]: 'Weitere Verbesserungen & Fixes',
        [Locales.EsES]: 'Otras mejoras y correcciones',
        [Locales.PlPL]: 'Inne ulepszenia i poprawki',
        [Locales.DaDK]: 'Andre forbedringer og rettelser',
        [Locales.SvSE]: 'Övriga förbättringar och korrigeringar',
        [Locales.NbNO]: 'Andre forbedringer og rettelser',
        [Locales.ItIT]: 'Altri miglioramenti e correzioni',
        [Locales.FrFR]: 'Autres améliorations et corrections',
        [Locales.PtPT]: 'Outras melhorias e correções',
        [Locales.PtBR]: 'Outras melhorias e correções',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'Calendar navigation',
            [Locales.NlNL]: 'Agendanavigatie',
            [Locales.DeDE]: 'Kalender-Navigation',
            [Locales.EsES]: 'Navegación del calendario',
            [Locales.PlPL]: 'Nawigacja kalendarza',
            [Locales.DaDK]: 'Kalendernavigation',
            [Locales.SvSE]: 'Kalendernavigering',
            [Locales.NbNO]: 'Kalendernavigasjon',
            [Locales.ItIT]: 'Navigazione del calendario',
            [Locales.FrFR]: 'Navigation du calendrier',
            [Locales.PtPT]: 'Navegação do calendário',
            [Locales.PtBR]: 'Navegação do calendário',
          },
          description: {
            [Locales.EnUS]:
              "Open the calendar and a menu appears to show or hide what's on it in one click.",
            [Locales.NlNL]:
              'Open de agenda en er verschijnt een menu om met één klik te tonen of te verbergen wat erop staat.',
            [Locales.DeDE]:
              'Öffne den Kalender und ein Menü erscheint, mit dem du mit einem Klick ein- oder ausblendest, was darauf steht.',
            [Locales.EsES]:
              'Abre el calendario y aparece un menú para mostrar u ocultar lo que contiene con un clic.',
            [Locales.PlPL]:
              'Otwórz kalendarz, a pojawi się menu, w którym jednym kliknięciem pokażesz lub ukryjesz to, co się na nim znajduje.',
            [Locales.DaDK]:
              'Åbn kalenderen, og en menu dukker op, hvor du med ét klik kan vise eller skjule, hvad der er på den.',
            [Locales.SvSE]:
              'Öppna kalendern så visas en meny där du med ett klick visar eller döljer det som finns i den.',
            [Locales.NbNO]:
              'Åpne kalenderen, så dukker det opp en meny der du med ett klikk viser eller skjuler det som er på den.',
            [Locales.ItIT]:
              'Apri il calendario e compare un menu per mostrare o nascondere con un clic ciò che contiene.',
            [Locales.FrFR]:
              "Ouvre le calendrier et un menu apparaît pour afficher ou masquer en un clic ce qu'il contient.",
            [Locales.PtPT]:
              'Abre o calendário e aparece um menu para mostrar ou ocultar com um clique o que lá está.',
            [Locales.PtBR]:
              'Abra o calendário e um menu aparece para mostrar ou ocultar com um clique o que está nele.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Trash',
            [Locales.NlNL]: 'Prullenbak',
            [Locales.DeDE]: 'Papierkorb',
            [Locales.EsES]: 'Papelera',
            [Locales.PlPL]: 'Kosz',
            [Locales.DaDK]: 'Papirkurv',
            [Locales.SvSE]: 'Papperskorg',
            [Locales.NbNO]: 'Papirkurv',
            [Locales.ItIT]: 'Cestino',
            [Locales.FrFR]: 'Corbeille',
            [Locales.PtPT]: 'Lixo',
            [Locales.PtBR]: 'Lixeira',
          },
          description: {
            [Locales.EnUS]:
              'Trash moved out of the main list to an icon at the bottom right of the sidebar.',
            [Locales.NlNL]:
              'De prullenbak is uit de hoofdlijst verhuisd naar een icoon rechtsonder in de zijbalk.',
            [Locales.DeDE]:
              'Der Papierkorb ist aus der Hauptliste zu einem Symbol unten rechts in der Seitenleiste gewandert.',
            [Locales.EsES]:
              'La papelera sale de la lista principal y pasa a un icono en la parte inferior derecha de la barra lateral.',
            [Locales.PlPL]:
              'Kosz przeniesiono z głównej listy do ikony w prawym dolnym rogu paska bocznego.',
            [Locales.DaDK]:
              'Papirkurven er flyttet ud af hovedlisten til et ikon nederst til højre i sidepanelet.',
            [Locales.SvSE]:
              'Papperskorgen har flyttats från huvudlistan till en ikon längst ner till höger i sidofältet.',
            [Locales.NbNO]:
              'Papirkurven er flyttet ut av hovedlisten til et ikon nederst til høyre i sidepanelet.',
            [Locales.ItIT]:
              "Il Cestino è passato dall'elenco principale a un'icona in basso a destra nella barra laterale.",
            [Locales.FrFR]:
              'La Corbeille a quitté la liste principale pour une icône en bas à droite de la barre latérale.',
            [Locales.PtPT]:
              'O Lixo saiu da lista principal para um ícone no canto inferior direito da barra lateral.',
            [Locales.PtBR]:
              'A Lixeira saiu da lista principal e foi para um ícone no canto inferior direito da barra lateral.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Habit list',
            [Locales.NlNL]: 'Gewoontelijst',
            [Locales.DeDE]: 'Gewohnheitsliste',
            [Locales.EsES]: 'Lista de hábitos',
            [Locales.PlPL]: 'Lista nawyków',
            [Locales.DaDK]: 'Vaneliste',
            [Locales.SvSE]: 'Vanelista',
            [Locales.NbNO]: 'Vaneliste',
            [Locales.ItIT]: 'Elenco delle abitudini',
            [Locales.FrFR]: 'Liste des habitudes',
            [Locales.PtPT]: 'Lista de hábitos',
            [Locales.PtBR]: 'Lista de hábitos',
          },
          description: {
            [Locales.EnUS]:
              'A habit past its end date shows "Ended on" and the date in red under its name.',
            [Locales.NlNL]:
              'Een gewoonte na haar einddatum toont "Beëindigd op" en de datum in rood onder haar naam.',
            [Locales.DeDE]:
              'Eine Gewohnheit nach ihrem Enddatum zeigt „Beendet am“ und das Datum in Rot unter ihrem Namen.',
            [Locales.EsES]:
              'Un hábito que pasó su fecha de fin muestra "Finalizado el" y la fecha en rojo debajo de su nombre.',
            [Locales.PlPL]:
              'Nawyk po dacie zakończenia pokazuje „Zakończono” i datę na czerwono pod nazwą.',
            [Locales.DaDK]:
              'En vane efter sin slutdato viser "Afsluttet" og datoen med rødt under navnet.',
            [Locales.SvSE]:
              'En vana efter sitt slutdatum visar "Avslutad" och datumet i rött under namnet.',
            [Locales.NbNO]:
              'En vane etter sluttdatoen viser "Avsluttet" og datoen i rødt under navnet.',
            [Locales.ItIT]:
              'Un\'abitudine oltre la data di fine mostra "Terminata il" e la data in rosso sotto il nome.',
            [Locales.FrFR]:
              'Une habitude passée sa date de fin affiche « Terminée le » et la date en rouge sous son nom.',
            [Locales.PtPT]:
              'Um hábito após a data de fim mostra "Terminado a" e a data a vermelho por baixo do nome.',
            [Locales.PtBR]:
              'Um hábito após a data de término mostra "Encerrado em" e a data em vermelho abaixo do nome.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Habit dialog',
            [Locales.NlNL]: 'Gewoontedialoog',
            [Locales.DeDE]: 'Gewohnheitsdialog',
            [Locales.EsES]: 'Diálogo del hábito',
            [Locales.PlPL]: 'Okno nawyku',
            [Locales.DaDK]: 'Vanedialog',
            [Locales.SvSE]: 'Vanedialog',
            [Locales.NbNO]: 'Vanedialog',
            [Locales.ItIT]: "Finestra dell'abitudine",
            [Locales.FrFR]: "Fenêtre de l'habitude",
            [Locales.PtPT]: 'Janela do hábito',
            [Locales.PtBR]: 'Janela do hábito',
          },
          description: {
            [Locales.EnUS]:
              'Open the habit and the Upcoming tab reads "No active schedule", with a button straight to its settings.',
            [Locales.NlNL]:
              'Open de gewoonte en het tabblad Planning toont "Geen actief schema", met een knop rechtstreeks naar de instellingen.',
            [Locales.DeDE]:
              'Öffne die Gewohnheit und der Tab Demnächst zeigt „Kein aktiver Plan“, mit einem Button direkt zu ihren Einstellungen.',
            [Locales.EsES]:
              'Abre el hábito y la pestaña Próximas muestra "Sin programación activa", con un botón directo a sus ajustes.',
            [Locales.PlPL]:
              'Otwórz nawyk, a zakładka Nadchodzące pokaże „Brak aktywnego harmonogramu” z przyciskiem prosto do ustawień.',
            [Locales.DaDK]:
              'Åbn vanen, og fanen Kommende viser "Ingen aktiv plan" med en knap direkte til dens indstillinger.',
            [Locales.SvSE]:
              'Öppna vanan så visar fliken Kommande "Inget aktivt schema", med en knapp direkt till dess inställningar.',
            [Locales.NbNO]:
              'Åpne vanen, så viser fanen Kommende "Ingen aktiv plan", med en knapp rett til innstillingene.',
            [Locales.ItIT]:
              'Apri l\'abitudine e la scheda Prossime mostra "Nessuna programmazione attiva", con un pulsante diretto alle impostazioni.',
            [Locales.FrFR]:
              "Ouvre l'habitude et l'onglet À venir affiche « Aucune programmation active », avec un bouton direct vers ses paramètres.",
            [Locales.PtPT]:
              'Abre o hábito e o separador Próximas mostra "Sem programação ativa", com um botão direto para as suas definições.',
            [Locales.PtBR]:
              'Abra o hábito e a aba Em breve mostra "Nenhuma programação ativa", com um botão direto para suas configurações.',
          },
        },
      ],
    },
  ],
};
